import { useEffect, useRef, useState } from "react";

import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { type Combobox as ComboboxPrimitive } from "@base-ui/react";
import { ComboboxChipsInput } from "@vega-core/components/shadcn/combobox";

import { DRAGGABLE_COMBOBOX_CHIP_EVENT_TYPE } from "../constants";
import {
  type DraggableComboboxChipMetaData,
  type SearchMultiSelectItem,
} from "../types";

import { DropIndicator } from "./DropIndicator";

/**
 * The sole purpose of this component is to show the drop indicator when a chip is dragged into a "blank space"
 * The chip is not supposed to be "dropped into an input"
 */
export function DropableComboboxChipsInput({
  fieldName,
  item,
  ...props
}: ComboboxPrimitive.Input.Props & {
  fieldName: string;
  item?: SearchMultiSelectItem;
}) {
  const dropTargetRef = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState<Edge | null>(null);

  useEffect(() => {
    const dropTargetElement = dropTargetRef.current as HTMLElement;

    return dropTargetForElements({
      element: dropTargetElement,
      getData({ input, element }) {
        if (!item) {
          return {
            fieldName,
            eventType: DRAGGABLE_COMBOBOX_CHIP_EVENT_TYPE,
            isDropableInput: true,
          };
        }

        return attachClosestEdge(
          {
            fieldName,
            item,
            eventType: DRAGGABLE_COMBOBOX_CHIP_EVENT_TYPE,
            isDropableInput: true,
          } satisfies DraggableComboboxChipMetaData,
          {
            element,
            input,
            allowedEdges: ["left"],
          },
        );
      },
      onDrag: ({ self }) => {
        setEdge(extractClosestEdge(self.data));
      },
      onDragLeave: () => {
        setEdge(null);
      },
      onDrop: () => {
        setEdge(null);
      },
    });
  }, [fieldName, item]);

  return (
    <div
      // negative margin needed here to extend the possible drop area of the input
      // Exact 11px needed here to match with draggable chips's height
      className="relative px-3 -mx-3 py-[11px] -my-[11px] flex-[1_0_auto] min-w-16"
      ref={dropTargetRef}
    >
      <ComboboxChipsInput {...props} />
      {edge ? <DropIndicator edge={edge} /> : null}
    </div>
  );
}
